import { GoogleMap, InfoWindow, Marker, MarkerClusterer } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as AlertIcon } from '../../../assets/icons/alert-icon.svg';
import AlertStationIcon from '../../../assets/icons/dashboard/alert-station-locator.svg';
import { ReactComponent as RightArrow } from '../../../assets/icons/dashboard/right-arrow.svg';
import { ReactComponent as UserIcon } from '../../../assets/icons/dashboard/user-icon.svg';
import { ReactComponent as LocationIcon } from '../../../assets/icons/location-icon.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { brandIcons, brandImage } from '../../../config/branding';
import { useGoogleMaps } from '../../../contexts/google-map-context/GoogleMapContent';
import { StationDetails, MapLocationProperties } from '../../../interface/dashboard.interface';
import AutoCompleteSelectField from '../../../shared-components/auto-complete-select-field/AutoCompleteSelectField';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import dashboard from '../../../utils/api/dashboard';

interface StationProperties {
  id: string;
  position: { lat: number; lng: number }; // Ensures two elements: [latitude, longitude]
  alert: boolean;
  name: string;
  address: string;
}

/**
 * Function
 * @param t
 * @returns
 */
const easeFunction = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 3 / 2); // Example easing function (cubic ease)

/**
 * Map component to station location
 */
function DashboardMap() {
  const { isLoaded } = useGoogleMaps();
  const navigate = useNavigate();
  const [showCards, setShowCards] = useState<boolean>(true);
  const [searchStation, setSearchStation] = useState<string | number>('');
  const [singleStationDetails, setSingleStationDetails] = useState<StationDetails>({
    _id: '',
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    opensAt: '', // ISO 8601 string format
    closesAt: '', // ISO 8601 string format
    isAvailable: false,
    connectors: [], // Array of connector types
    totalChargingPoints: 0,
    clientsOnStation: 0,
  });
  const [selectedMarker, setSelectedMarker] = useState<StationProperties | null>(null);

  const [stationLocation, setStationLocation] = useState<StationProperties[]>([]);

  const [center, setCenter] = useState({
    lat: 41.8781, // Example: San Francisco
    lng: -87.6298,
  });
  const mapReference = useRef<google.maps.Map | null>(null);

  /**
   * Function to handle smooth animation
   * @param latLng
   * @returns
   */
  function panToSmoothly(latLng: { lat: number; lng: number }) {
    if (!mapReference.current) {
      throw new Error('Map object is required for panning.');
    }

    const currentCenter = mapReference.current.getCenter();
    if (!(currentCenter instanceof google.maps.LatLng)) {
      console.warn('Unexpected center type. Animation might not work as expected.');
      return;
    }

    const currentLat = currentCenter.lat();
    const currentLng = currentCenter.lng();

    const distance = google.maps.geometry.spherical.computeDistanceBetween(currentCenter, latLng);

    if (distance < 10) {
      mapReference.current.panTo(latLng);
      return;
    }

    const duration = Math.min(distance * 0.1, 1000); // Adjust duration based on distance
    const stepCount = 60;
    const interval = duration / stepCount;

    let step = 0;

    const intervalId = setInterval(() => {
      const progress = easeFunction(step / stepCount);
      const lat = currentLat + (latLng.lat - currentLat) * progress;
      const lng = currentLng + (latLng.lng - currentLng) * progress;

      mapReference.current?.panTo({ lat, lng });
      step += 1;

      if (step >= stepCount) {
        clearInterval(intervalId);
      }
    }, interval);
  }

  /**
   * Function to filter the station list by name or address
   * @param name
   * @param value
   */
  const handleFilterByStationDetails = (name: string, value: string | number) => {
    setSearchStation(value);

    const matchedStation: StationProperties | undefined = stationLocation.find(
      (station: StationProperties) => station.name === value,
    );

    if (matchedStation) {
      const firstStationPosition = matchedStation.position;
      panToSmoothly(firstStationPosition);
    }
  };

  /**
   * Function to fetch single station details
   */
  const handleFetchSingleStation = async (marker: StationProperties) => {
    setCenter(marker.position);
    setSelectedMarker(marker);
    setShowCards(false);
    const response = await dashboard.getStationDetails(marker.id);
    if (response.data.length) {
      setSingleStationDetails(response.data?.[0]);
    }
  };

  /**
   * Function to fetch station latitude and longitude
   */
  const fetchStationLatLong = async () => {
    const response = await dashboard.getStationMap();
    const mapLocations = response?.data || [];
    if (mapLocations?.length > 0) {
      const availableStations = mapLocations.map((location: MapLocationProperties) => ({
        id: location._id,
        position: { lat: parseFloat(location?.latitude), lng: parseFloat(location?.longitude) },
        alert: location?.isFault,
        name: location?.name,
        address: location?.address,
      }));

      setStationLocation(availableStations);

      setCenter({
        lat: mapLocations.length ? parseFloat(mapLocations[0].latitude) : 41.8781,
        lng: mapLocations.length ? parseFloat(mapLocations[0].longitude) : -87.6298,
      });
    }
  };

  useEffect(() => {
    /**
     * Function to map details one by one
     */
    const getCardsCount = async () => {
      try {
        await fetchStationLatLong();
      } catch (error) {
        console.error(error);
      }
    };

    getCardsCount();
  }, []);

  const containerStyle = {
    width: '100%',
    height: '100%',
  };

  return (
    <div className="dashboard-map-wrap" onMouseLeave={() => setShowCards(true)}>
      {/* <h1>Overview</h1> */}
      <div className="dashboard-map-container">
        {/* Google map container */}
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={7}
            onLoad={(map) => {
              mapReference.current = map; // Assign the map instance to the reference
            }}>
            {/* Marker Clusterer for grouping nearby markers */}
            <MarkerClusterer>
              {(clusterer) => (
                <>
                  {stationLocation.map((marker) => (
                    <Marker
                      key={marker.id}
                      position={marker.position}
                      clusterer={clusterer}
                      icon={
                        marker.alert
                          ? {
                              url: AlertStationIcon,
                              scaledSize: new window.google.maps.Size(76, 47), // Width, Height
                            }
                          : {
                              url: brandIcons.stationLocator,
                              scaledSize: new window.google.maps.Size(44, 44), // Width, Height
                            }
                      }
                      onClick={() => handleFetchSingleStation(marker)} // Open popup on marker click
                    />
                  ))}
                </>
              )}
            </MarkerClusterer>
            {/* Popup for marker */}
            {selectedMarker && (
              <InfoWindow
                position={selectedMarker.position}
                onCloseClick={() => setSelectedMarker(null)}>
                {selectedMarker.alert ? (
                  <div className="map-station-details-wrap alert-wrap">
                    <div className="map-station-details-container">
                      <span>
                        <AlertIcon />
                      </span>
                      <p>Station Performance Alerts</p>
                    </div>
                  </div>
                ) : (
                  <div className="map-station-active-container">
                    <div className="map-station-details">
                      <div className="details-header">
                        <div className="details-header-image-wrap">
                          <img
                            src={brandImage.chargingDevice}
                            alt=""
                            className="details-header-image"
                          />
                        </div>
                        <div className="name-and-status">
                          <h2>{singleStationDetails.name}</h2>
                          {singleStationDetails.isAvailable ? (
                            <div className="status-wrap active">
                              <div className="status-point" />
                              <p>Active</p>
                            </div>
                          ) : (
                            <div className="status-wrap in-active">
                              <div className="status-point in-active" />
                              <p>Inactive</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="address-details">
                        <div className="place-with-icon">
                          <span />
                          {/* To-do - need to show country here or need to confirm */}
                          <span>
                            <LocationIcon />
                          </span>
                        </div>
                        <p>{singleStationDetails.address}</p>
                      </div>
                      <div className="user-details">
                        <span>
                          <UserIcon />
                        </span>
                        <span className="count">{singleStationDetails.clientsOnStation}</span>
                        <span className="message">Client on station</span>
                      </div>
                    </div>
                    <div className="map-station-center-border" />
                    <div className="map-station-availability-wrap">
                      <div className="availability-status-wrap">
                        <div className="each-availability">
                          <span>
                            <brandIcons.ActiveClockIcon />
                          </span>
                          <p>{`${ConvertUTCtoHHMM(singleStationDetails.opensAt)} - ${ConvertUTCtoHHMM(singleStationDetails.closesAt)}`}</p>
                        </div>
                        <div className="each-availability">
                          <span>
                            <brandIcons.ActiveDeviceIcon />
                          </span>
                          <p>{singleStationDetails.totalChargingPoints} Charging Points</p>
                        </div>
                        <div className="each-availability">
                          <span>
                            <brandIcons.ActiveConnectorIcon />
                          </span>
                          <p>{singleStationDetails.connectors.join(', ')}</p>
                        </div>
                      </div>
                      <div className="view-more-button">
                        <MyButton
                          label="View more"
                          buttonType="submit"
                          disableHoverEffect
                          svgIcon={<RightArrow />}
                          onClickFunc={() => navigate('../station-management')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </InfoWindow>
            )}
          </GoogleMap>
        ) : (
          <div className="dashboard-map-loader">
            <BatteryLoader />
          </div>
        )}
        {/* Absolute search box */}
        <div className="map-search-wrap" role="presentation" onClick={() => setShowCards(false)}>
          <AutoCompleteSelectField
            inputLabel=""
            placeholder="Search"
            name="search"
            selectedValue={searchStation}
            onChangeValue={handleFilterByStationDetails}
            svgIcon={<SearchIcon />}
            options={stationLocation.map((station) => ({
              label: `${station.name} (${station.address})`,
              value: station.name,
            }))}
          />
        </div>
        {/* Absolute background color */}
        {showCards && <div className="absolute-background" />}
      </div>
    </div>
  );
}

export default DashboardMap;
